/**
 * Login Screen 2: OTP Entry
 *
 * Second screen for OTP-based authentication
 */

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Link,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

interface LoginScreen2OTPProps {
  identifier: string;
  onSubmit: (otp: string) => Promise<void>;
  onResend: () => Promise<void>;
  error: string | null;
}

export function LoginScreen2OTP({
  identifier,
  onSubmit,
  onResend,
  error,
}: LoginScreen2OTPProps) {
  const [otp, setOtp] = useState('');
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onSubmit(otp);
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      await onResend();
    } finally {
      setResending(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Box>
        <Typography variant="h5" component="h1" gutterBottom>
          Enter Verification Code
        </Typography>
        <Typography variant="body2" color="text.secondary">
          We sent a code to: {identifier}
        </Typography>
      </Box>

      {error && <Alert severity="error">{error}</Alert>}

      <form onSubmit={handleSubmit}>
        <Stack spacing={2}>
          <TextField
            fullWidth
            label="Verification Code"
            variant="outlined"
            value={otp}
            onChange={(e) => setOtp(e.target.value)}
            placeholder="Enter 6-digit code"
            disabled={loading}
            autoFocus
            required
            inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
          />

          <Button
            type="submit"
            variant="contained"
            size="large"
            fullWidth
            disabled={loading || otp.length !== 6}
          >
            {loading ? (
              <>
                <CircularProgress size={24} sx={{ mr: 1 }} />
                Verifying...
              </>
            ) : (
              'Verify Code'
            )}
          </Button>

          <Typography variant="body2" align="center">
            Didn&apos;t receive the code?{' '}
            <Link
              component="button"
              type="button"
              onClick={handleResend}
              disabled={resending}
            >
              {resending ? 'Sending...' : 'Resend'}
            </Link>
          </Typography>
        </Stack>
      </form>
    </Stack>
  );
}
